import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MovixLogo from "@/components/MovixLogo";
import { COMPANY } from "@/lib/company";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, Phone, Mail, MapPin, ShieldCheck, Lock } from "lucide-react";

const LOGIN_BG = "https://images.unsplash.com/photo-1601929862217-f1bf94503333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzg3MTE0NjB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, error } = useAuth();
  const { t } = useT();
  const [email, setEmail] = useState("admin@locadora.com");
  const [password, setPassword] = useState("admin123");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await login(email, password);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen w-full relative bg-black flex flex-col overflow-hidden" data-testid="login-page">
      {/* Background image with overlays */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${LOGIN_BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40" />
      {/* subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">
            <span className="w-1.5 h-1.5 bg-[#F5A623] inline-block" />
            {t("login.systemVersion")}
          </div>
        </div>
        <LanguageSwitcher />
      </header>

      {/* Main grid: hero on left, card on right (or stacked on mobile) */}
      <main className="relative z-10 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 px-6 lg:px-12 pb-12 items-center">
        {/* Hero / Brand */}
        <section className="lg:col-span-7 text-white space-y-10">
          <div className="rise">
            <MovixLogo variant="light" showTagline size="xl" />
          </div>

          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/15 bg-white/5 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 bg-[#F5A623] inline-block" />
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/80">
                Premium Fleet Management
              </span>
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl xl:text-6xl tracking-tighter leading-[0.95]">
              {t("login.heroTitle1")} <br className="hidden md:block" />
              <span className="text-[#F5A623]">{t("login.heroTitle2")}</span>
            </h1>
            <p className="text-white/65 text-sm md:text-base leading-relaxed max-w-xl">
              {t("login.heroDescription")}
            </p>
          </div>

          {/* Trust badges */}
          <div className="hidden md:grid grid-cols-3 gap-0 border-l border-t border-white/10 max-w-xl">
            <Badge icon={ShieldCheck} title="SECURE" sub="Encrypted access" />
            <Badge icon={Lock} title="GDPR" sub="Privacy compliant" />
            <Badge icon={ArrowRight} title="REAL-TIME" sub="Live dashboard" />
          </div>
        </section>

        {/* Login card */}
        <section className="lg:col-span-5 flex justify-center lg:justify-end">
          <form
            onSubmit={onSubmit}
            className="relative w-full max-w-md bg-white shadow-[0_20px_70px_-10px_rgba(0,0,0,0.4)] border border-zinc-200"
            data-testid="login-form"
          >
            {/* accent bar */}
            <div className="h-1 bg-[#F5A623]" />

            <div className="p-8 lg:p-10 space-y-7">
              <div className="space-y-2">
                <div className="overline">{t("login.restrictedAccess")}</div>
                <h2 className="font-display font-bold text-2xl tracking-tighter">{t("login.signIn")}</h2>
                <p className="text-sm text-zinc-500">{t("login.useCredentials")}</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="email" className="overline">{t("login.email")}</label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@movix.com"
                    className="rounded-none border-zinc-300 h-11 focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-0"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="password" className="overline">{t("login.password")}</label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="rounded-none border-zinc-300 h-11 focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-0"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {error && (
                <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="group w-full h-12 rounded-none bg-black hover:bg-[#F5A623] text-white font-medium tracking-tight transition-colors"
                data-testid="login-submit-button"
              >
                {submitting ? (
                  <Loader2 className="animate-spin w-4 h-4" />
                ) : (
                  <span className="inline-flex items-center gap-2">
                    {t("login.submit")}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                )}
              </Button>

              <div className="pt-5 border-t border-zinc-200 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">
                <span>{t("login.defaultCreds")}</span>
                <span className="text-zinc-600">admin@locadora.com</span>
              </div>
            </div>
          </form>
        </section>
      </main>

      {/* Bottom contact ribbon */}
      <footer className="relative z-10 border-t border-white/10 bg-black/40 backdrop-blur-md">
        <div className="px-6 lg:px-12 py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] font-mono text-white/70">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 hover:text-[#F5A623] transition-colors">
              <Phone className="w-3 h-3 text-[#F5A623]" /> {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 hover:text-[#F5A623] transition-colors">
              <Mail className="w-3 h-3 text-[#F5A623]" /> {COMPANY.email}
            </a>
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-3 h-3 text-[#F5A623]" /> {COMPANY.address}
            </span>
          </div>
          <div className="uppercase tracking-[0.2em] text-white/40 text-[10px]">
            © {new Date().getFullYear()} {COMPANY.fullName}
          </div>
        </div>
      </footer>
    </div>
  );
}

function Badge({ icon: Icon, title, sub }) {
  return (
    <div className="border-r border-b border-white/10 -ml-px -mt-px px-4 py-3 bg-white/[0.03] backdrop-blur-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#F5A623]" />
        <div className="text-[10px] font-mono font-semibold tracking-[0.25em] text-white">{title}</div>
      </div>
      <div className="text-[10px] text-white/50 font-mono">{sub}</div>
    </div>
  );
}
